/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { InvoiceFilters } from '@/components/invoices/InvoiceFilters'
import { InvoiceTable } from '@/components/invoices/InvoiceTable'
import { InvoiceEditDialog } from '@/components/invoices/InvoiceEditDialog'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  getInvoices,
  deleteInvoice,
  getCenters,
  getDocumentStatuses,
} from '@/api/invoices/invoices.api'
import type {
  InvoiceFilters as InvoiceFiltersType,
  AccountingDocument,
  InvoiceSortField,
} from '@/types/invoices.types'

export default function IncomingInvoicesPage() {
  const queryClient = useQueryClient()

  // State pro filtry a řazení
  const [filters, setFilters] = useState<InvoiceFiltersType>({
    page: 1,
    perPage: 20,
    sortBy: 'date_due',
    sortOrder: 'ASC',
  })

  // State pro editační dialog
  const [editingInvoice, setEditingInvoice] = useState<AccountingDocument | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  // Načtení faktur
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => getInvoices(filters),
  })

  // Mutace pro smazání faktury
  const deleteMutation = useMutation({
    mutationFn: deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast.success('Faktura byla úspěšně smazána')
    },
    onError: () => {
      toast.error('Chyba při mazání faktury')
    },
  })

  // Načtení číselníků pro zobrazení názvů v tabulce
  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: getCenters,
  })

  const { data: statuses } = useQuery({
    queryKey: ['statuses'],
    queryFn: getDocumentStatuses,
  })

  const handleFiltersChange = (newFilters: InvoiceFiltersType) => {
    setFilters({ ...newFilters, page: 1 })
  }

  const handleSort = (field: InvoiceSortField) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === 'ASC' ? 'DESC' : 'ASC',
    }))
  }

  const handleEdit = (invoice: AccountingDocument) => {
    setEditingInvoice(invoice)
    setIsEditDialogOpen(true)
  }

  const handleDelete = async (id: number) => {
    deleteMutation.mutate(id)
  }

  const handleEditSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['invoices'] })
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  return (
    <div className="container mx-auto py-6 max-w-7xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Přijaté faktury</h1>
          <p className="text-muted-foreground mt-1">
            Správa přijatých faktur s možností filtrování, řazení a editace
          </p>
        </div>

        {/* Filtry */}
        <InvoiceFilters filters={filters} onFiltersChange={handleFiltersChange} />

        {/* Tabulka */}
        {isLoading ? (
          <div className="bg-white rounded-lg border p-8 text-center text-muted-foreground">
            Načítám faktury...
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg border p-8 text-center text-red-600">
            Chyba při načítání faktur
          </div>
        ) : (
          <>
            <InvoiceTable
              invoices={data?.data || []}
              centers={centers}
              statuses={statuses}
              onEdit={handleEdit}
              onDelete={handleDelete}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSort={handleSort}
            />

            {/* Paginace */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="flex items-center justify-between bg-white rounded-lg border p-4">
                <div className="text-sm text-muted-foreground">
                  Stránka {data.meta.page} z {data.meta.totalPages} (celkem{' '}
                  {data.meta.total} faktur)
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(data.meta.page - 1)}
                    disabled={data.meta.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Předchozí
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(data.meta.page + 1)}
                    disabled={data.meta.page === data.meta.totalPages}
                  >
                    Další
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Editační dialog */}
      <InvoiceEditDialog
        invoice={editingInvoice}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={handleEditSuccess}
      />
    </div>
  )
}
